import { useCallback, useEffect, useState } from 'react'
import { ADMIN_SECTIONS } from './sections'
import type { AdminSectionId, AdminSectionProps } from './types'
import {
  loadRoleNavigation,
  updateRoleNavigation
} from '@core/auth/roleNavigation'
import {
  ROLE_PRESETS,
  sectionsForRole,
  type RoleNavigationConfig,
  type RolePresetId
} from '@core/auth/rolePresets'
import { btn, btnBase, btnSize, checkbox, cx, hint } from '@ui/classes'
import { okBanner } from '@ui/admin'
import { AdminCard } from './AdminCard'

export function RolesSection({ perms }: AdminSectionProps) {
  // Реестр импортируется циклически (sections → RolesSection → sections),
  // поэтому читаем его при рендере, а не на уровне модуля.
  const editableSections = ADMIN_SECTIONS.filter((section) => section.id !== 'roles')
  const [config, setConfig] = useState<RoleNavigationConfig | null>(null)
  const [draft, setDraftState] = useState<Partial<Record<RolePresetId, AdminSectionId[]>>>({})
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState(false)

  const reload = useCallback(async () => {
    const loaded = await loadRoleNavigation()
    setConfig(loaded)
    setDraftState({})
    setSaved(false)
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  if (!config) {
    return <p className={hint}>Загружаем раскладку…</p>
  }

  if (!perms.canManageCloud) {
    return <p className={hint}>Ролевую навигацию настраивает директор.</p>
  }

  const effective = (role: RolePresetId): AdminSectionId[] => {
    const value = sectionsForRole(role, config)
    // `all` для директора разворачиваем в список: галочки должны быть явными.
    return value === 'all' ? editableSections.map((s) => s.id) : value
  }

  const current = (role: RolePresetId): AdminSectionId[] =>
    draft[role] ?? effective(role)

  const toggle = (role: RolePresetId, id: AdminSectionId) => {
    const list = current(role)
    const next = list.includes(id) ? list.filter((s) => s !== id) : [...list, id]
    setDraftState((prev) => ({ ...prev, [role]: next }))
    setSaved(false)
  }

  const save = async (role: RolePresetId) => {
    setBusy(true)
    try {
      const next = await updateRoleNavigation(role, current(role))
      setConfig(next)
      setDraftState((prev) => {
        const copy = { ...prev }
        delete copy[role]
        return copy
      })
      setSaved(true)
    } finally {
      setBusy(false)
    }
  }

  const reset = async (role: RolePresetId) => {
    setBusy(true)
    try {
      const next = await updateRoleNavigation(role, undefined)
      setConfig(next)
      setDraftState((prev) => {
        const copy = { ...prev }
        delete copy[role]
        return copy
      })
      setSaved(true)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <AdminCard id="roles.navigation" title="Навигация по ролям" defaultOpen>
        <p className={hint}>
          Пресеты фиксированные: кассир, старший смены, директор. Здесь меняется только
          набор разделов, которые видит роль. Права доступа не расширяются — скрытый
          правами раздел не появится.
        </p>
        {saved && <div className={okBanner}>Сохранено. Изменения уйдут в облако и на кассы.</div>}
      </AdminCard>

      {ROLE_PRESETS.map((preset) => {
        const selected = current(preset.id)
        const changed = draft[preset.id] != null
        return (
          <AdminCard
            key={preset.id}
            id={`roles.${preset.id}`}
            title={preset.label}
            testId={`role-preset-${preset.id}`}
          >
            <div className="mb-2 flex flex-wrap items-center justify-end gap-2">
              <div className="flex gap-2">
                <button
                  type="button"
                  className={cx(btnBase, btn.secondary, btnSize.sm)}
                  onClick={() => void reset(preset.id)}
                  disabled={busy || config.byRole[preset.id] == null}
                  title="Вернуть набор разделов из пресета"
                >
                  Сбросить к пресету
                </button>
                <button
                  type="button"
                  className={cx(btnBase, btn.primary, btnSize.sm)}
                  onClick={() => void save(preset.id)}
                  disabled={busy || !changed}
                >
                  Сохранить
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {editableSections.map((section) => (
                <label
                  key={`${preset.id}-${section.id}`}
                  className="flex cursor-pointer items-center gap-2 text-small text-ink"
                >
                  <input
                    type="checkbox"
                    className={checkbox}
                    checked={selected.includes(section.id)}
                    onChange={() => toggle(preset.id, section.id)}
                  />
                  {section.label}
                </label>
              ))}
            </div>
          </AdminCard>
        )
      })}
    </div>
  )
}
